import { useEffect, useRef, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Bookmark, BookmarkActionViewMode } from '../../types/api';
import {
  useLikeBookmark,
  useArchiveBookmark,
  useUnarchiveBookmark,
  useDeleteBookmark,
  useMoveToFolder,
} from '../../services/bookmarkActions';
import { useCurrentUser } from '../../services/userHooks';
import { useBookmarkEdit } from '../../contexts/BookmarkEditContext';
import { useBookmarkPageContent } from '../../contexts/BookmarkPageContext';
import { useNavigateToNextBookmark } from '../../hooks/useNavigateToNextBookmark';
import FolderMenu from './FolderMenu';
import MenuItem from './MenuItem';
import ReaderTagPopover from '../Reader/ReaderTagPopover';
import Icon from '../ui/Icon';
import { SHORTCUT_KEYS } from '../KeyboardShortcutsModal/shortcuts';

/** Which pane of the menu is showing. */
export type BookmarkActionsView = 'menu' | 'folders' | 'tags';

interface BookmarkActionsMenuProps {
  bookmark: Bookmark;
  viewMode?: BookmarkActionViewMode;
  currentFolderId?: string;
  /** Pane to mount into — 'folders'/'tags' skip the top level (m/t shortcuts). */
  initialView?: BookmarkActionsView;
  onClose: () => void;
}

function BookmarkActionsMenu({
  bookmark,
  viewMode = 'home',
  currentFolderId,
  initialView = 'menu',
  onClose,
}: BookmarkActionsMenuProps) {
  const { currentUser: userData } = useCurrentUser();
  const likeMutation = useLikeBookmark();
  const archiveMutation = useArchiveBookmark();
  const unarchiveMutation = useUnarchiveBookmark();
  const deleteMutation = useDeleteBookmark();
  const moveMutation = useMoveToFolder();

  const [isLiked, setIsLiked] = useState(bookmark.liked);
  const [showFolderMenu, setShowFolderMenu] = useState(initialView === 'folders');
  const [showTagPopover, setShowTagPopover] = useState(initialView === 'tags');
  const menuRef = useRef<HTMLDivElement>(null);

  const isActionPending =
    likeMutation.isPending ||
    archiveMutation.isPending ||
    unarchiveMutation.isPending ||
    deleteMutation.isPending ||
    moveMutation.isPending;

  // Two things silently strip focus off the menu, taking the arrow keys and the
  // accelerators below with them: coming back from the folder/tag sub-view
  // unmounts the focused button, and an item that disables itself mid-mutation
  // gets blurred by the browser. Either way focus lands on <body>, and
  // FloatingFocusManager only restores it when the popover itself mounts, not
  // when its contents change. So put it back:
  //   - on a view swap, onto the first item, which is where the eye goes;
  //   - mid-action, onto the container, so the cursor doesn't jump to a
  //     neighbouring item and strand the user somewhere they didn't choose.
  useEffect(() => {
    const menu = menuRef.current;
    if (!menu || menu.contains(document.activeElement)) return;
    const firstItem = isActionPending
      ? null
      : // eslint-disable-next-line lingui/no-unlocalized-strings
        menu.querySelector<HTMLElement>('button:not([disabled])');
    (firstItem ?? menu).focus({ preventScroll: true });
  }, [showFolderMenu, showTagPopover, isActionPending]);
  const { openEditModal } = useBookmarkEdit();
  const navigateToNext = useNavigateToNextBookmark();
  const bookmarkPage = useBookmarkPageContent();

  const isArchived = viewMode === 'archive';
  const isActiveInReader =
    bookmarkPage?.activeBookmark != null &&
    bookmarkPage.readerLayout !== '2col' &&
    String(bookmarkPage.activeBookmark.id) === String(bookmark.id);

  const handleLike = () => {
    const newLikedState = !isLiked;
    setIsLiked(newLikedState);
    likeMutation.mutate(
      { bookmarkId: bookmark.id, action: newLikedState ? 'like' : 'unlike' },
      {
        onError: () => {
          setIsLiked(bookmark.liked);
        },
      },
    );
  };

  const handleArchive = () => {
    onClose();
    if (isActiveInReader) {
      navigateToNext(bookmark.id);
    }
    if (isArchived) {
      unarchiveMutation.mutate({ bookmarkId: bookmark.id });
    } else {
      archiveMutation.mutate({ bookmarkId: bookmark.id });
    }
  };

  const handleDelete = () => {
    if (window.confirm(t`Are you sure you want to delete this article?`)) {
      onClose();
      if (isActiveInReader) {
        navigateToNext(bookmark.id);
      }
      deleteMutation.mutate({ bookmarkId: bookmark.id });
    }
  };

  const handleMove = (folderId: string) => {
    // If this article is open in the reader, moving it to another section
    // removes it from the current list, so advance the reader to the next
    // article first (matches archive/delete).
    if (isActiveInReader) {
      navigateToNext(bookmark.id);
    }
    if (folderId === 'home') {
      // Moving to home uses the 'home' action, not 'move'
      unarchiveMutation.mutate({ bookmarkId: bookmark.id });
    } else {
      moveMutation.mutate({ bookmarkId: bookmark.id, folderId });
    }
  };

  const handleEdit = () => {
    openEditModal(bookmark);
    onClose();
  };

  const canMove = !!userData?.folders && userData.folders.length > 0;
  const canArchive = viewMode !== 'liked';

  /**
   * Mnemonic accelerators, using the same letters as the reader shortcuts, so
   * an open menu can be driven entirely from the keyboard.
   *
   * preventDefault + stopPropagation matter here: the popover is portalled in
   * the DOM but still a React child of the bookmark row, so without them the
   * row's own shortcut handler would run the action a second time.
   */
  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;

    const run = (action: () => void) => {
      event.preventDefault();
      event.stopPropagation();
      action();
    };

    // Lower-cased so Caps Lock doesn't disable the accelerators.
    switch (event.key.toLowerCase()) {
      case SHORTCUT_KEYS.like:
        if (likeMutation.isPending) return;
        return run(handleLike);
      case SHORTCUT_KEYS.tag:
        return run(() => setShowTagPopover(true));
      case SHORTCUT_KEYS.move:
        if (!canMove || moveMutation.isPending) return;
        return run(() => setShowFolderMenu(true));
      case SHORTCUT_KEYS.archive:
        if (!canArchive || archiveMutation.isPending || unarchiveMutation.isPending)
          return;
        return run(handleArchive);
      case SHORTCUT_KEYS.edit:
        return run(handleEdit);
      case SHORTCUT_KEYS.delete:
        if (deleteMutation.isPending) return;
        return run(handleDelete);
    }
  };

  if (showFolderMenu && userData?.folders) {
    return (
      <FolderMenu
        folders={userData.folders}
        currentFolderId={currentFolderId}
        viewMode={viewMode}
        onSelect={handleMove}
        onBack={() => setShowFolderMenu(false)}
      />
    );
  }

  if (showTagPopover) {
    return (
      <ReaderTagPopover
        bookmarkId={bookmark.id}
        initialBookmark={bookmark}
        closePopover={() => {
          setShowTagPopover(false);
          onClose();
        }}
      />
    );
  }

  return (
    // Wider than the labels alone need: the trailing shortcut hints would
    // otherwise wrap "Move to folder" onto a second line.
    // tabIndex is the fallback focus target when there is no enabled item.
    <div
      ref={menuRef}
      tabIndex={-1}
      className="w-52 focus:outline-none"
      onKeyDown={handleMenuKeyDown}
    >
      <MenuItem
        onClick={handleLike}
        disabled={likeMutation.isPending}
        icon={<Icon name={isLiked ? 'like-filled' : 'like'} />}
        shortcut={SHORTCUT_KEYS.like}
      >
        {isLiked ? <Trans>Unlike</Trans> : <Trans>Like</Trans>}
      </MenuItem>

      <MenuItem
        onClick={() => setShowTagPopover(true)}
        icon={<Icon name="tag" />}
        shortcut={SHORTCUT_KEYS.tag}
      >
        <Trans>Tag</Trans>
      </MenuItem>

      {canMove && (
        <MenuItem
          onClick={() => setShowFolderMenu(true)}
          disabled={moveMutation.isPending}
          icon={<Icon name="folder" />}
          shortcut={SHORTCUT_KEYS.move}
        >
          <Trans>Move to folder</Trans>
        </MenuItem>
      )}

      {canArchive && (
        <MenuItem
          onClick={handleArchive}
          disabled={archiveMutation.isPending || unarchiveMutation.isPending}
          icon={<Icon name="archive" />}
          shortcut={SHORTCUT_KEYS.archive}
        >
          {isArchived ? <Trans>Unarchive</Trans> : <Trans>Archive</Trans>}
        </MenuItem>
      )}

      <MenuItem
        onClick={handleEdit}
        icon={<Icon name="edit" />}
        shortcut={SHORTCUT_KEYS.edit}
      >
        <Trans>Edit</Trans>
      </MenuItem>

      <MenuItem
        onClick={handleDelete}
        disabled={deleteMutation.isPending}
        icon={<Icon name="trash" />}
        shortcut={SHORTCUT_KEYS.delete}
      >
        <Trans>Delete</Trans>
      </MenuItem>
    </div>
  );
}

export default BookmarkActionsMenu;
